import { PlusOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { Button, message, Pagination, Spin } from 'antd';
import classNames from 'classnames';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReferralEmpty as ReferralEmptyIcon } from 'common/components/svgs';
import CommissionPlanCard from '~pages/dashboard/referral/commission-plan/plan-card';
import CommissionPlanEditor from '~pages/dashboard/referral/commission-plan/plan-editor';
import { QUERY_COMMISSION_PLANS } from './queries';
import showElementByAuth from '~helpers/auth';
import { platformEntity, entityAction } from '~constants';

const CommissionPlanList = () => {
  const { t } = useTranslation();

  const [planEditorType, setPlanEditorType] = useState('create');
  const [editingPlan, setEditingPlan] = useState(null);
  const [showPlanEditor, setShowPlanEditor] = useState(false);
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
  });

  const {
    loading,
    data: commissionPlanListResponse,
    refetch: refetchCommissionPlan,
  } = useQuery(QUERY_COMMISSION_PLANS, {
    variables: {
      pageNumber: pagination.pageNumber,
      pageSize: pagination.pageSize,
      sortBy: [
        { sortField: 'DEFAULT', sortDirection: 'DESCENDING' },
        { sortField: 'UPDATED_AT', sortDirection: 'DESCENDING' },
      ],
    },
    onError: e => {
      message.error(e?.toString());
    },
  });

  const commissionPlanList = useMemo(() => {
    return commissionPlanListResponse?.referralCommissionPlans?.edges || [];
  }, [commissionPlanListResponse]);

  const handleCloseModal = () => {
    setShowPlanEditor(false);
    refetchCommissionPlan();
  };

  const handleCreatePlan = () => {
    setPlanEditorType('create');
    setEditingPlan(null);
    setShowPlanEditor(true);
  };

  const handleEditPlan = plan => {
    setPlanEditorType('edit');
    setEditingPlan(plan);
    setShowPlanEditor(true);
  };
  return (
    <>
      <div className="commission-plan">
        <div className="commission-plan__header">
          <h1>{t('cms.referral.commission_plan.title')}</h1>
          <If
            condition={showElementByAuth(
              platformEntity.REFERRAL_COMMISSION_PLAN,
              entityAction.CREATE,
            )}
          >
            {commissionPlanList.length ? (
              <button
                className="commission-plan__header__button"
                type="button"
                onClick={handleCreatePlan}
              >
                <PlusOutlined
                  className="commission-plan__header__button__icon"
                  style={{ fontSize: '12px', marginLeft: '4px' }}
                />
                {t('cms.referral.commission_plan.button.add')}
              </button>
            ) : null}
          </If>
        </div>

        <section
          className={classNames('commission-plan__body', {
            'commission-plan__body--bg-white': commissionPlanList.length === 0 && !loading,
          })}
        >
          <Choose>
            <When condition={loading}>
              <div className="commission-plan__spin-container">
                <Spin />
              </div>
            </When>
            <When condition={commissionPlanList.length}>
              <For of={commissionPlanList} each="plan">
                <CommissionPlanCard
                  key={plan.node.id}
                  data={plan.node}
                  handleEditPlan={plan => handleEditPlan(plan)}
                />
              </For>
            </When>
            <When condition={commissionPlanList.length === 0}>
              <div className="commission-plan__blank-tips">
                <div className="commission-plan__blank-tips__content">
                  <div>
                    <ReferralEmptyIcon />
                  </div>
                  <span className="commission-plan__blank-tips__tip">
                    {t('cms.referral.commission_plan.blanktip')}
                  </span>
                  <If
                    condition={showElementByAuth(
                      platformEntity.REFERRAL_COMMISSION_PLAN,
                      entityAction.CREATE,
                    )}
                  >
                    <Button
                      className="commission-plan__blank-tips__blank-create-btn"
                      type="primary"
                      onClick={handleCreatePlan}
                      size="large"
                    >
                      {t('cms.referral.commission_plan.button.add')}
                    </Button>
                  </If>
                </div>
              </div>
            </When>
          </Choose>
        </section>
        <section
          className={classNames('commission-plan__footer', {
            'commission-plan__footer--hidden': loading,
          })}
        >
          <div className="commission-plan__footer__pagination">
            <Pagination
              showSizeChanger
              onShowSizeChange={(_current, size) =>
                setPagination({
                  pageSize: size,
                  pageNumber: 1,
                })
              }
              pageSizeOptions={['10', '20', '30', '40']}
              total={commissionPlanListResponse?.referralCommissionPlans?.totalCount || 0}
              pageSize={pagination.pageSize}
              current={pagination.pageNumber}
              onChange={(pageNumber, pageSize) => {
                setPagination({
                  pageSize: pageSize,
                  pageNumber: pageNumber,
                });
              }}
            />
          </div>
        </section>

        <If condition={showPlanEditor}>
          <CommissionPlanEditor
            modalType={planEditorType}
            editPlan={editingPlan}
            handleCloseModal={handleCloseModal}
          />
        </If>
      </div>
    </>
  );
};

export default CommissionPlanList;
